import type {
  BlendPointRef,
  ChamferFeatureDefinition,
  FilletFeatureDefinition,
  SelectionItemRef,
} from '@puppycad/cad-core';
import {
  buildChamferModelTopologyBindings,
  buildFilletModelTopologyBindings,
  ChamferFeatureDefinitionModel,
  FilletFeatureDefinitionModel,
  FeatureFace,
  FeatureLine,
  FeaturePoint,
  normalizeChamferIgnoredOptions,
} from '@puppycad/cad-core';
import { clonePlainData } from '@/core/features/blend/command-utils';
import { flattenFaceSetItems } from '@/core/features/blend/stage-two';

export type ActiveBlendKind = 'fillet' | 'chamfer' | null;
export type BlendObject = FeatureFace | FeatureLine | FeaturePoint;

/**
 * 构建点引用在 UI 选择流程中的稳定身份。
 *
 * @param pointRef 点引用。
 * @returns 稳定身份字符串。
 */
export function resolveBlendPointIdentityKey(pointRef: BlendPointRef): string {
  const identity = pointRef.subEntityId
    ?? pointRef.persistentRef
    ?? pointRef.runtimeTopologyHint?.subEntityId
    ?? pointRef.runtimeTopologyHint?.persistentRef
    ?? pointRef.point.join(',');
  return `${pointRef.bodyObjectId}:${identity}`;
}

/**
 * 归一化错误消息。
 *
 * @param error 原始错误。
 * @param fallback 兜底消息。
 * @returns 可直接展示给用户的错误文本。
 */
export function normalizeErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * 构建圆角草稿定义与引用绑定。
 *
 * @param input 圆角草稿输入。
 * @returns 定义模型与序列化绑定。
 */
export function buildFilletDraft(input: {
  definition: FilletFeatureDefinition;
  upstreamBodyObjectId: string | null;
  editingFeatureId: string | null;
}) {
  const definitionModel = FilletFeatureDefinitionModel.fromParams({
    commandParams: {
      featureKind: 'fillet',
      blendMode: 'fillet',
      upstreamBodyObjectId: input.upstreamBodyObjectId ?? undefined,
      definition: clonePlainData(input.definition),
      editingFeatureId: input.editingFeatureId ?? undefined,
      referenceBindings: buildFilletModelTopologyBindings({
        upstreamBodyObjectId: input.upstreamBodyObjectId ?? undefined,
        definition: input.definition,
      }),
    },
  });
  return {
    definitionModel,
    bindings: definitionModel.getCommandParams().referenceBindings ?? [],
  };
}

/**
 * 构建倒角草稿定义与引用绑定。
 *
 * @param input 倒角草稿输入。
 * @returns 定义模型与序列化绑定。
 */
export function buildChamferDraft(input: {
  definition: ChamferFeatureDefinition;
  upstreamBodyObjectId: string | null;
  editingFeatureId: string | null;
}) {
  const normalizedDefinition = normalizeChamferIgnoredOptions(input.definition);
  const definitionModel = ChamferFeatureDefinitionModel.fromParams({
    commandParams: {
      featureKind: 'chamfer',
      blendMode: 'chamfer',
      upstreamBodyObjectId: input.upstreamBodyObjectId ?? undefined,
      definition: clonePlainData(normalizedDefinition),
      editingFeatureId: input.editingFeatureId ?? undefined,
      referenceBindings: buildChamferModelTopologyBindings({
        upstreamBodyObjectId: input.upstreamBodyObjectId ?? undefined,
        definition: normalizedDefinition,
      }),
    },
  });
  return {
    definitionModel,
    bindings: definitionModel.getCommandParams().referenceBindings ?? [],
  };
}

/**
 * 解析圆角当前选择项。
 *
 * @param definition 圆角定义。
 * @returns 当前选择项。
 */
export function resolveFilletItems(definition: FilletFeatureDefinition) {
  if (definition.type === 'face') {
    const itemByRef = new Map(flattenFaceSetItems(definition.faceSets).map((item) => [item.persistentRef, item]));
    return Array.from(itemByRef.values());
  }
  return definition.items ?? [];
}

/**
 * 解析倒角当前选择项。
 *
 * @param definition 倒角定义。
 * @returns 当前选择项。
 */
export function resolveChamferItems(definition: ChamferFeatureDefinition) {
  if (definition.type !== 'face-face') {
    return definition.items ?? [];
  }
  const selectionItems = flattenFaceSetItems(definition.faceSets);
  const itemByRef = new Map(selectionItems.map((item) => [item.persistentRef, item]));
  return Array.from(itemByRef.values());
}

/**
 * 从当前圆角/倒角 definition 推导上游实体 ID。
 *
 * 设计说明：
 * 1. 只有选择框中的边/面/顶点（或面组）决定圆角/倒角作用实体，helpPoint 与部分边参考点不参与推导；
 * 2. 主选择集为空时表示命令尚未绑定实体，应允许用户重新选择任意实体；
 * 3. 该函数是 `upstreamBodyObjectId` 的唯一真相来源，避免首次点击即写入缓存造成 stale 锁。
 *
 * @param activeKind 当前激活命令类型。
 * @param filletDefinition 圆角定义。
 * @param chamferDefinition 倒角定义。
 * @returns 唯一上游实体 ID；未绑定或存在冲突时返回 `null`。
 */
export function resolveBlendUpstreamBodyObjectId(
  activeKind: Exclude<ActiveBlendKind, null>,
  filletDefinition: FilletFeatureDefinition,
  chamferDefinition: ChamferFeatureDefinition,
): string | null {
  const items = activeKind === 'fillet'
    ? resolveFilletItems(filletDefinition)
    : resolveChamferItems(chamferDefinition);
  const bodyObjectIds = Array.from(new Set(
    items
      .map((item) => item.bodyObjectId)
      .filter((value): value is string => typeof value === 'string' && value.length > 0),
  ));

  return bodyObjectIds.length === 1 ? bodyObjectIds[0]! : null;
}

/**
 * 构建选择对象去重 key。
 *
 * @param object 选择对象。
 * @returns 去重 key。
 */
export function buildBlendObjectDedupKey(object: BlendObject): string {
  const typedObject = object as BlendObject & {
    objectId?: string;
    edgeIndex?: number;
    faceIndex?: number;
    vertexIndex?: number;
  };
  if (typedObject instanceof FeatureLine) {
    return `edge:${typedObject.objectId ?? 'unknown'}:${typedObject.edgeIndex ?? 'unknown'}`;
  }
  if (typedObject instanceof FeatureFace) {
    return `face:${typedObject.objectId ?? 'unknown'}:${typedObject.faceIndex ?? 'unknown'}`;
  }
  return `vertex:${typedObject.objectId ?? 'unknown'}:${typedObject.vertexIndex ?? 'unknown'}`;
}

/**
 * 构建选择项去重 key。
 *
 * @param itemRef 选择项引用。
 * @returns 去重 key。
 */
export function buildBlendSelectionItemDedupKey(itemRef: SelectionItemRef): string {
  return `${itemRef.kind}:${itemRef.persistentRef}`;
}

/**
 * 解析可以配置部分边线参数的边引用。
 *
 * @param items 主选择项集合。
 * @returns 边引用集合。
 */
export function resolvePartialEdgeItemRefs(
  items: Array<{ kind: string; persistentRef: string }>,
): string[] {
  return items
    .filter((item) => item.kind === 'edge')
    .map((item) => item.persistentRef);
}
